import { useEffect, useMemo, useState } from "react";
import { reminderConfig } from "definitions/constants/config";
import { NewHireJourney } from "models/v2/newHireJourney";
import { RemindRecency, ReminderRow, StakeholderItem } from "../types";
import { useStakeholderGroups } from "./useStakeholderGroups";

// Refresh "now" every 5 minutes so recency labels (`never`/`recent`/`stale`) flip
// without the user reloading. Done as state rather than Date.now() inside useMemo
// to satisfy React Compiler purity rules.
const NOW_REFRESH_MS = 5 * 60 * 1000;

interface UseReminderRowsOptions {
  enabled?: boolean;
  currentUserId?: string;
  /** When provided, only tasks with these IDs are considered. */
  filterTaskIds?: Set<number>;
}

interface UseReminderRowsResult {
  rows: ReminderRow[];
  youRow?: ReminderRow;
  excludedUserStakeholder?: StakeholderItem;
  stakeholdersWithOpenTasks: StakeholderItem[];
  isLoading: boolean;
  isFetched: boolean;
}

function deriveRecency(lastReminderAt: string | undefined, now: number): RemindRecency {
  if (!lastReminderAt) return "never";
  const hoursSince = (now - new Date(lastReminderAt).getTime()) / (1000 * 60 * 60);
  return hoursSince < reminderConfig.recentlyRemindedWindowHours ? "recent" : "stale";
}

// * 1000 ensures any overdue count outranks any combination of never-reminded + open-task scores.
function urgencyScore(row: ReminderRow): number {
  return row.overdueCount * 1000
    + (row.recency === "never" ? 100 : 0)
    + (row.overdueCount + row.urgentCount + row.activeCount);
}

function toRow(item: StakeholderItem, isSelf: boolean, now: number): ReminderRow {
  return {
    ...item,
    recency: deriveRecency(item.lastReminderAt, now),
    isSelf,
  };
}

export function useReminderRows(
  newHireJourney: NewHireJourney,
  options: UseReminderRowsOptions = {},
): UseReminderRowsResult {
  const { enabled = true, currentUserId, filterTaskIds } = options;

  const { stakeholdersWithOpenTasks, excludedUserStakeholder, isLoading, isFetched } = useStakeholderGroups(
    newHireJourney,
    { enabled, excludeUserId: currentUserId, filterTaskIds },
  );

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), NOW_REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  return useMemo(() => {
    const rows = stakeholdersWithOpenTasks
      .map((item) => toRow(item, false, now))
      .sort((a, b) => {
        const score = urgencyScore(b) - urgencyScore(a);
        if (score !== 0) return score;
        return a.user.fullName.localeCompare(b.user.fullName);
      });

    const youRow = excludedUserStakeholder && excludedUserStakeholder.tasks.length > 0
      ? toRow(excludedUserStakeholder, true, now)
      : undefined;

    return {
      rows,
      youRow,
      excludedUserStakeholder,
      stakeholdersWithOpenTasks,
      isLoading,
      isFetched,
    };
  }, [stakeholdersWithOpenTasks, excludedUserStakeholder, isLoading, isFetched, now]);
}
